import React, { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import '../../styles/modules/react-datepicker.scss'
import tableStyles from '../../styles/modules/table.module.scss'
import styles from '../../styles/modules/pre-making-detail/pre-making-schedule-manager.module.scss'
import moment from 'moment'

interface Props {
  templateType: 'weekly' | 'custom'
  selectedDates: string[]
  isEditing: boolean
  editDates: Date[]
  onDatesChange: (dates: Date[]) => void
}

const PreMakingTemplateScheduleManager: React.FC<Props> = ({
  templateType,
  selectedDates,
  isEditing,
  editDates,
  onDatesChange,
}) => {
  // 編集モード切替時の初期化フラグ
  const [isInitialized, setIsInitialized] = useState(false)
  // DatePickerの選択状態
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  // 編集モード切替時に日付を初期化（一度だけ実行）
  useEffect(() => {
    if (
      templateType === 'custom' &&
      isEditing &&
      !isInitialized &&
      selectedDates.length > 0
    ) {
      const dates = selectedDates
        .map(dateStr => new Date(dateStr))
        .sort((a, b) => a.getTime() - b.getTime())
      onDatesChange(dates)
      setIsInitialized(true)
    } else if (!isEditing) {
      // 編集モード終了時に初期化フラグをリセット
      setIsInitialized(false)
    }
  }, [templateType, isEditing, selectedDates, isInitialized, onDatesChange])

  // カスタムテンプレート以外は適用日管理を表示しない
  if (templateType !== 'custom') {
    return null
  }

  // 日付追加
  const handleAddDate = (date: Date) => {
    if (!date) {
      return
    }

    // 同じ日付が既に存在するかチェック（時刻を無視して日付のみで比較）
    const dateString = date.toDateString()
    const isDuplicate = editDates.some(
      existingDate => existingDate.toDateString() === dateString
    )

    if (!isDuplicate) {
      const newDates = [...editDates, date].sort(
        (a, b) => a.getTime() - b.getTime()
      )
      onDatesChange(newDates)
    }

    // DatePickerの選択をクリア
    setSelectedDate(null)
  }

  // 日付削除
  const handleRemoveDate = (index: number) => {
    const newDates = editDates
      .filter((_, i) => i !== index)
      .sort((a, b) => a.getTime() - b.getTime())
    onDatesChange(newDates)
  }

  return (
    <>
      {/* 適用日セクションタイトル */}
      <h3 className={tableStyles.title}>適用日</h3>

      <div className={styles.container}>
        {isEditing ? (
          <div className={styles.editMode}>
            {/* 日付選択 */}
            <div className={styles.datePickerSection}>
              <label className={styles.label}>新しい適用日を追加</label>
              <DatePicker
                selected={selectedDate}
                onChange={date => {
                  setSelectedDate(date)
                  if (date) {
                    handleAddDate(date)
                  }
                }}
                placeholderText="日付を選択してください"
                dateFormat="yyyy年MM月dd日 (eee)"
                locale="ja"
                minDate={new Date()}
                className={styles.datePicker}
              />
            </div>

            {/* 選択された日付一覧 */}
            <div className={styles.selectedDatesSection}>
              <label className={styles.label}>選択された適用日</label>
              {editDates.length > 0 ? (
                <div className={styles.dateList}>
                  {editDates.map((date, index) => (
                    <div key={index} className={styles.dateItem}>
                      <span className={styles.dateText}>
                        {moment(date).format('YYYY年MM月DD日')}
                      </span>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => handleRemoveDate(index)}
                        aria-label="削除"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  適用日が選択されていません
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.viewMode}>
            {selectedDates.length > 0 ? (
              <div className={styles.dateList}>
                {selectedDates.map((dateStr, index) => (
                  <div key={index} className={styles.dateItem}>
                    <span className={styles.dateText}>
                      {moment(dateStr).format('YYYY年MM月DD日')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                適用日が設定されていません
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default PreMakingTemplateScheduleManager
